import React, { useState } from "react";
import { Row, Col, Input, Select, Tooltip, Button, Upload, message } from "antd";
import { InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { ValidateEmail } from "utils/GlobalFunctions";

const { Option } = Select;
const { TextArea } = Input;

const Step1GeneralInfo = ({ wizardData, setWizardData, activityList }) => {
  const [errorField, setErrorField] = useState([]);

  const handleChange = (field, value) => {
    setWizardData({ ...wizardData, [field]: value });
    // Clear error for this field
    if (errorField.includes(field)) {
      setErrorField(errorField.filter((x) => x !== field));
    }
  };

  const handleActivityChange = (value) => {
    const selectedActivity = activityList.find((x) => parseInt(x.ID) === value);
    if (selectedActivity) {
      setWizardData({
        ...wizardData,
        activity: value,
        sport_id: selectedActivity.TYPE,
        sport: selectedActivity.NAME,
      });
      if (errorField.includes("activity")) {
        setErrorField(errorField.filter((x) => x !== "activity"));
      }
    }
  };

  const handleFileUpload = (info) => {
    if (info.file.status === "uploading") {
      return;
    }
    if (info.file.status === "done" || info.file.originFileObj) {
      setWizardData({
        ...wizardData,
        infoPageFile: info.file.originFileObj || info.file,
      });
      message.success(`${info.file.name} file selected successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const uploadProps = {
    name: "file",
    accept: ".pdf,.doc,.docx",
    beforeUpload: (file) => {
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("File must be smaller than 5MB!");
        return false;
      }
      return false; // Prevent auto-upload, we'll handle it manually
    },
    onChange: handleFileUpload,
    showUploadList: true,
    maxCount: 1,
  };

  return (
    <div className="wizard-step-card">
      <h3 className="wizard-step-title">General Information</h3>
      <p className="wizard-step-description">
        Let's start by setting up the basic information for your registration.
      </p>

      <Row gutter={[16, 24]}>
        <Col xs={24} sm={12}>
          <div className="wizard-form-label">
            Registration Name <span style={{ color: "red" }}>*</span>
          </div>
          <Input
            value={wizardData.name}
            placeholder="e.g., Summer Camp 2024"
            className={
              errorField.includes("name") ? "common-input-error" : "common-input"
            }
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </Col>

        <Col xs={24} sm={12}>
          <div className="wizard-form-label">
            Activity Type <span style={{ color: "red" }}>*</span>
          </div>
          <Select
            showSearch
            placeholder="Select activity type"
            className={
              errorField.includes("activity")
                ? "common-select-error"
                : "common-select"
            }
            value={wizardData.activity}
            onChange={handleActivityChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {activityList.map((x, index) => (
              <Option value={parseInt(x.ID)} key={index}>
                {x.NAME}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24}>
          <div className="wizard-form-label">
            Registration Description
          </div>
          <TextArea
            value={wizardData.description}
            placeholder="Provide a brief description of this registration (optional)"
            rows={3}
            onChange={(e) => handleChange("description", e.target.value)}
          />
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            This description helps you identify this registration in the dashboard
          </div>
        </Col>

        <Col xs={24} sm={12}>
          <div className="wizard-form-label">
            Information Page Document
          </div>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              Select File (PDF, DOC, DOCX)
            </Button>
          </Upload>
          <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
            Upload additional information document for participants (optional)
          </div>
        </Col>

        <Col xs={24} sm={12}>
          <div className="wizard-form-label">
            Admin Email Address <span style={{ color: "red" }}>*</span>
            <Tooltip title="Email address displayed to let users know who to contact with questions">
              <InfoCircleOutlined
                style={{ marginLeft: 8, color: "#888", cursor: "help" }}
              />
            </Tooltip>
          </div>
          <Input
            type="email"
            value={wizardData.admin_email}
            placeholder="admin@example.com"
            className={
              errorField.includes("admin_email")
                ? "common-input-error"
                : "common-input"
            }
            onChange={(e) => handleChange("admin_email", e.target.value)}
            onBlur={() => {
              if (
                wizardData.admin_email &&
                !ValidateEmail(wizardData.admin_email)
              ) {
                setErrorField([...errorField, "admin_email"]);
                message.error("Please enter a valid email address");
              }
            }}
          />
        </Col>
      </Row>

      <div className="wizard-info-box" style={{ marginTop: 24 }}>
        <p>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          All fields marked with <span style={{ color: "red" }}>*</span> are required
          to proceed to the next step.
        </p>
      </div>
    </div>
  );
};

export default Step1GeneralInfo;
